import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Input,
  Modal,
  Table,
  TableColumnsType,
  Typography,
} from 'antd';
import { debounce } from 'lodash';

import { LoadingStatus } from '@/core/enums/loadingStatus';
import { useAppDispatch, useAppSelector } from '@/core/redux/hooks';
import { airExchangeTableSelectors } from '@/core/redux/slices/airExchangeTable/selectors';
import {
  airExchangeTableActions,
  IAirExchangeTableItem,
  IAirExchangeTableListFetchPayload,
} from '@/core/redux/slices/airExchangeTable/slice';
import { appSettingsActions } from '@/core/redux/slices/app/slice';
import { systemSelectors } from '@/core/redux/slices/smokeExtraction/system/selectors';
import { systemActions } from '@/core/redux/slices/smokeExtraction/system/slice';
import { dateToSystemPage } from '@/core/utils/dateUtils';
import { declination } from '@/helpers/converter';

import { AirExchangeEmpty } from './AirExchangeEmpty';
import { CreatorOfTable } from './CreatorOfTable';

const { Text, Title } = Typography;

const defaultPageSize = 10;

interface Ifilters {
  search?: string;
  order_by?: string;
  updated_at__lte?: string;
  updated_at__gte?: string;
  size?: number;
  page?: number;
}

export const AirExchangeTablePage: React.FC = () => {
  const [isModal, setIsModal] = useState<any>({});
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [filtersState, setFiltersState] = useState<Ifilters>({
    size: defaultPageSize,
    page: 1,
    order_by: '-updated_at',
  });

  const listRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const airExchangeTableList = useAppSelector(
    airExchangeTableSelectors.airExchangeTableList
  );
  const total = airExchangeTableList?.total;

  const updateSystemLock = useAppSelector(systemSelectors.updateSystemLock);

  useEffect(() => {
    const payload: IAirExchangeTableListFetchPayload = {
      size: filtersState?.size,
      page: filtersState?.page,
    };

    if (filtersState?.order_by) payload.order_by = filtersState?.order_by;
    if (filtersState?.search) payload.search = filtersState?.search;

    dispatch(airExchangeTableActions.fetchAirExchangeTableList(payload));

    listRef?.current?.scroll({ top: 0, behavior: 'smooth' });
  }, [filtersState, updateSystemLock]);

  useEffect(() => {
    if (updateSystemLock === LoadingStatus.LOADED) {
      dispatch(
        appSettingsActions.setAppMessage({
          type: 'success',
          content: 'Таблица успешно обновлена',
        })
      );
    }
  }, [updateSystemLock]);

  const handleFilterChange = (
    key: string,
    value: number | string | boolean | null
  ) => {
    setFiltersState((prev) => {
      return { ...prev, [key]: value };
    });
  };

  const debouncedSearch = debounce(handleFilterChange, 700);

  const handleRemove = (id: number) => {
    dispatch(systemActions.deleteSystem({ id }));
    setIsModal({});
  };

  const columns: TableColumnsType<IAirExchangeTableItem> = [
    {
      title: 'ID',
      dataIndex: 'id',
    },
    {
      title: 'Наименование расчета',
      dataIndex: 'name',
    },
    {
      title: 'Наименование объекта и проекта',
      dataIndex: 'project_name',
      render: (text, record, index) => {
        return (
          <div className='text-primary cursor-pointer'>
            Объект: {record.object_name}
            <br />
            Проект: {record.project_name}
          </div>
        );
      },
    },

    {
      title: 'Последнее изменение',
      dataIndex: 'updated_at',
      render: (text) => dateToSystemPage(text),
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      defaultSortOrder: 'descend',
    },

    {
      title: '',
      dataIndex: '',
      render: (text, record) => (
        <div className='flex gap-2'>
          <Button
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              setIsModal({
                show: true,
                title: `Вы уверены, что хотите удалить Таблицу воздухообмена?`,
                handleOk: () => handleRemove(Number(record.id)),
                okText: 'Да, удалить',
                okType: 'danger',
                body: (
                  <div>
                    <span className='font-semibold mr-2'>
                      Наименование расчета:
                    </span>
                    {record?.name}
                    <br />
                    <span className='font-semibold mr-2'>
                      Наименование проекта:
                    </span>
                    {record?.project_name}
                    <br />
                    <span className='font-semibold mr-2'>
                      Название объекта:
                    </span>
                    {record?.object_name}
                  </div>
                ),
              });
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className='h-full'>
      {openModal && (
        <CreatorOfTable openModal={openModal} setOpenModal={setOpenModal} />
      )}

      {airExchangeTableList?.items?.length === 0 && !filtersState?.search ? (
        <AirExchangeEmpty setOpenModal={setOpenModal} />
      ) : airExchangeTableList ? (
        <div className='h-full p-6  overflow-auto' ref={listRef}>
          <div className='bg-white p-6 pt-0'>
            <Table
              rowClassName='items-center'
              columns={columns}
              dataSource={airExchangeTableList?.items}
              rowKey='id'
              title={() => (
                <div className='flex items-center justify-between'>
                  <Title level={5}>Таблица воздухообмена</Title>
                  <div className='flex gap-4'>
                    <Input
                      className='w-[264px]'
                      onChange={({ target }) =>
                        debouncedSearch('search', target.value)
                      }
                      placeholder='Найти'
                    />

                    <Button type='primary' onClick={() => setOpenModal(true)}>
                      <PlusOutlined /> Создать
                    </Button>
                  </div>
                </div>
              )}
              onChange={(pagination, filters, sorter: any) => {
                sorter?.field &&
                  handleFilterChange(
                    'order_by',
                    `${sorter?.order === 'ascend' ? '' : '-'}${sorter?.field}`
                  );

                pagination.current &&
                  handleFilterChange('page', pagination.current);
              }}
              onRow={(record) => ({
                onClick: () => navigate(`/air-exchange-table/${record?.id}`),
              })}
              pagination={{
                total: total ?? 0,
                showTotal: (total) => declination('calculation', total),
                defaultPageSize: defaultPageSize,
                defaultCurrent: 1,
                onShowSizeChange(_, size) {
                  handleFilterChange('size', size);
                },
              }}
            />
          </div>
        </div>
      ) : null}
      {isModal?.show && (
        <Modal
          centered
          closable={false}
          title={isModal?.title}
          open={isModal?.show}
          onOk={isModal?.handleOk}
          okText={isModal?.okText}
          okType={isModal?.okType}
          onCancel={() => setIsModal({})}
        >
          {isModal?.body}
        </Modal>
      )}
    </div>
  );
};
